import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { createEventHook, noop } from '@vueuse/core';
import { store as vuexStore } from '@/store';

import * as CommsService from '@/common/bridge/modern-bridge/comms.service';
import { CommsCommand } from '@/common/bridge/modern-bridge/comms.enum';
import type { UserInformation } from '@/pleased-sdk/PleasedApi/models.pleased';
import { useAtomic } from '@/use/useAtomic';
import { PleasedApi } from '@/pleased-sdk/PleasedApi';
import { PleasedClient } from '@/pleased-sdk/PleasedClient';
import { capitalizeFirstLetters, isEmailValid, isPhoneNumber } from '@/common/utils';
import { getFallbackUserName } from '@/common/userDisplayName';
import { setChatbotContext, captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

export type DraftUser = Partial<
  Pick<
    UserInformation,
    'id' | 'token' | 'defaultName' | 'email' | 'phone' | 'name' | 'foreignId' | 'userLoyaltyTierBand'
  >
> & {
  /** Guest / open-request form draft (widget reads this; Vuex `guestFormData` is mirrored on write). */
  description?: string;
};

export type GuestFormDraftPayload = {
  name: string;
  email: string;
  phone: string;
  description: string;
};

export interface UserDetail extends Omit<Required<DraftUser>, 'description'> {
  description?: string;
  isLoading: boolean;
  displayName: string;
  isGuest: boolean;
  id: number | string;
  token: string;
}

export const useBotUser = defineStore('currentUser', () => {
  const currentUser = ref<UserInformation | null>();
  const guestUser = ref<DraftUser | null>(null);

  /**
   * If we get new information from the user, or
   * from client side, these are the changes to the user
   * object that are not yet synched with the pleased server.
   */
  const userUpdateDraft = ref<DraftUser | null>(null);

  const [checkUserAuthenticated, isCheckingToken] = useAtomic(async () => {
    // Missing token for guests is expected. Bridge failures during re_auth (already logged in) are not.
    const token = await CommsService.getResult(CommsCommand.execute.token).catch((error: unknown) => {
      if (isAuthenticated.value) {
        captureEventException(error, ErrorTrackingEvent.WidgetAuth);
      }
      return null;
    });

    if (!token) {
      return isAuthenticated.value ? logout() : void 0;
    }

    const apiUser = await PleasedApi.verifyAuthToken({ token });
    currentUser.value = apiUser;

    // Update the user token.
    PleasedClient.setConfig({ token: apiUser.token });

    vuexStore.commit('user/updatePleasedUser', apiUser);
    vuexStore.commit('user/updateUserGreyListed', { greyListed: apiUser.greyListed });
    vuexStore.commit('user/setUserPhoneOrEmailIfNotExists', {
      phone: apiUser.phone,
      email: apiUser.email,
      name: apiUser.name,
    });
    vuexStore.commit('user/setIsLogin', true);

    return apiUser;
  });

  // PleasedApi.setReauth(checkUserAuthenticated);

  const [loginViaToken, isGettingToken] = useAtomic(
    async ({ token, countryCode }: { token: string; countryCode: string }) => {
      // getPleasedTokenFromSportyToken ?
      const apiUser = await PleasedApi.getPleasedTokenFromSportyToken({ sportyToken: token, countryCode });
      vuexStore.commit('user/updatePleasedUser', apiUser);
      vuexStore.commit('user/updateUserGreyListed', { greyListed: apiUser.greyListed });
      currentUser.value = apiUser;

      // Update the user token.
      PleasedClient.setConfig({ token: apiUser.token });
    }
  );

  const [login, isLoggingIn] = useAtomic(async ({ userName, password }: { userName: string; password: string }) => {
    // await CommsService.getResult(CommsCommand.execute.login, { password, username: userName });
    // If the login is successful, we can now generate the token.
    await checkUserAuthenticated();
  });

  const logout = () => {
    // Update the user token. This will logout the user from pleased.
    PleasedClient.setConfig({ token: null });
    // Reset user store
    currentUser.value = null;
    userUpdateDraft.value = null;
    guestUser.value = null;
  };

  function setGuestDetail(details: Partial<DraftUser>) {
    if (currentUser.value) {
      userUpdateDraft.value = { ...userUpdateDraft.value, ...details };
    }
    guestUser.value = { ...(guestUser.value ?? {}), ...details };
  }

  function mergeGuestFormDraft(payload: GuestFormDraftPayload) {
    guestUser.value = {
      ...(guestUser.value ?? {}),
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      description: payload.description,
    };
  }

  function clearGuestFormDraft() {
    guestUser.value = {
      ...(guestUser.value ?? {}),
      name: '',
      email: '',
      phone: '',
      description: '',
    };
  }

  const setCurrentUser = (user: UserInformation) => {
    currentUser.value = user;
  };

  const setGuestUser = (guest: DraftUser | null) => {
    guestUser.value = guest;
    PleasedClient.setConfig({ token: guest?.token });
  };

  const isAuthenticating = computed(() => Boolean(isGettingToken.value || isLoggingIn.value || isCheckingToken.value));

  const authenticatedUser = computed(() => ({
    detail: currentUser.value,
    isAuthenticating: isAuthenticating.value,
  }));

  const user = computed<UserDetail>(() => {
    const userName = currentUser.value?.name ?? guestUser.value?.name ?? '';

    const nameIsEmail = isEmailValid(userName);
    const nameIsPhone = isPhoneNumber(userName);

    const isNameProvided = userName && !nameIsEmail && !nameIsPhone;
    const capitalizedName = isNameProvided ? capitalizeFirstLetters(userName) : '';

    const fallbackName = getFallbackUserName();
    const displayName = isNameProvided ? capitalizedName : fallbackName;

    if (currentUser.value)
      return {
        isLoading: isAuthenticating.value,
        displayName,
        defaultName: userUpdateDraft.value?.defaultName || currentUser.value.defaultName || '',
        name: userUpdateDraft.value?.name || currentUser.value.name || '',
        email: userUpdateDraft.value?.email || currentUser.value.email || '',
        phone: userUpdateDraft.value?.phone || currentUser.value.phone || '',
        foreignId: userUpdateDraft.value?.foreignId || currentUser.value.foreignId || '',
        id: currentUser.value.id,
        token: currentUser.value.token,
        greyListed: currentUser.value.greyListed || false,
        isGuest: false,
        userLoyaltyTierBand: userUpdateDraft.value?.userLoyaltyTierBand || currentUser.value.userLoyaltyTierBand || '',
        description: userUpdateDraft.value?.description ?? guestUser.value?.description,
      };
    return {
      isLoading: isAuthenticating.value,
      displayName,
      defaultName: guestUser.value?.defaultName || '',
      name: guestUser.value?.name || '',
      phone: guestUser.value?.phone || '',
      foreignId: guestUser.value?.foreignId || '',
      email: guestUser.value?.email || '',
      description: guestUser.value?.description,
      // TODO: when handle guest cases – it could have anon token after filling details
      token: '',
      id: 0,
      greyListed: false,
      isGuest: true,
      userLoyaltyTierBand: guestUser.value?.userLoyaltyTierBand || '',
    };
  });

  async function finishLoading() {
    await Promise.all([isGettingToken.value, isLoggingIn.value, isCheckingToken.value]).catch(noop);
  }

  const isAuthenticated = computed(() => !!currentUser.value);

  // Keep GlitchTip `is_guest` aligned after mid-session login / logout, not only on widget init.
  watch(isAuthenticated, (authenticated) => {
    setChatbotContext({ isGuest: !authenticated });
  });

  /**
   * Guest has completed identity (Vuex `updateGuestUser` / anonymous token on `guestUser`); hides “Continue as guest” on login.
   * Derived so it stays aligned with `isAuthenticated` and guest payload; Vuex `user/guestUser.filledDetails` remains the legacy write path.
   */
  const guestFilledDetails = computed(() => {
    if (isAuthenticated.value) return false;
    return Boolean(guestUser.value?.token);
  });

  /** Mirrors Vuex `user.bridgeWidgetLoginInProgress` (set by `Bridge.login`). */
  const bridgeWidgetLoginInProgress = computed(() => vuexStore.state.user.bridgeWidgetLoginInProgress);
  const displayName = computed(() => {
    const nameIsEmail = isEmailValid(user.value.name);
    const nameIsPhone = isPhoneNumber(user.value.name);

    const isNameProvided = user.value.name && !nameIsEmail && !nameIsPhone;

    // TODO: take from config
    const fallbackName = 'Tenn User';
    const name = isNameProvided ? user.value.name : fallbackName;

    return name;
  });

  const authUpdated = createEventHook<boolean>();

  watch(isAuthenticated, () => authUpdated.trigger(isAuthenticated.value));

  return {
    user,
    guestUser,
    guestFilledDetails,
    isAuthenticated,
    bridgeWidgetLoginInProgress,
    authenticatedUser,
    displayName,
    login,
    loginViaToken,
    logout,
    setGuestDetail,
    mergeGuestFormDraft,
    clearGuestFormDraft,
    setCurrentUser,
    setGuestUser,
    finishLoading,
    onAuth: authUpdated.on,
    checkUserAuthenticated,
  };
});
